import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import type { Locale } from "date-fns";
import {
  FileText,
  Download,
  Upload,
  CalendarDays,
  Clock,
  MessageSquare,
  User,
  Users,
  RotateCcw,
  ShieldAlertIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { LeaveApprovalButtons } from "./LeaveApprovalButtons";
import { apiCancelLeaveRequest, apiGetTeamLeaveOverlap, apiUploadLeaveDocument, apiReactivateEmployee, API_BASE, type LeaveRequest, type TeamLeaveOverlap } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDateLocale } from "@/hooks/useDateLocale";
import { getLeaveApiErrorMessage, getLeaveStatusLabel } from "@/lib/leave-i18n";

const STATUS_STYLES: Record<
  string,
  { bg: string; text: string; dot: string }
> = {
  Approved: {
    bg: "bg-green-100 dark:bg-green-900/30",
    text: "text-green-700 dark:text-green-400",
    dot: "bg-green-500",
  },
  Pending: {
    bg: "bg-yellow-100 dark:bg-yellow-900/30",
    text: "text-yellow-700 dark:text-yellow-400",
    dot: "bg-yellow-500",
  },
  "Pending Employee Document": {
    bg: "bg-amber-100 dark:bg-amber-900/30",
    text: "text-amber-700 dark:text-amber-400",
    dot: "bg-amber-500",
  },
  "Document Uploaded": {
    bg: "bg-blue-100 dark:bg-blue-900/30",
    text: "text-blue-700 dark:text-blue-400",
    dot: "bg-blue-500",
  },
  "HR Review Required": {
    bg: "bg-blue-100 dark:bg-blue-900/30",
    text: "text-blue-700 dark:text-blue-400",
    dot: "bg-blue-500",
  },
  Rejected: {
    bg: "bg-red-100 dark:bg-red-900/30",
    text: "text-red-700 dark:text-red-400",
    dot: "bg-red-500",
  },
  Cancelled: {
    bg: "bg-gray-100 dark:bg-gray-800/50",
    text: "text-gray-600 dark:text-gray-400",
    dot: "bg-gray-400",
  },
};

function StatusPill({ status, label }: { status: string; label: string }) {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.Cancelled;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${s.bg} ${s.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {label}
    </span>
  );
}

function fmt(dateStr: string | null, locale: Locale) {
  if (!dateStr) return "-";
  try {
    return format(new Date(dateStr), "d MMM yyyy", { locale });
  } catch {
    return dateStr;
  }
}

function initials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

interface LeaveDetailModalProps {
  request: LeaveRequest | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function LeaveDetailModal({
  request,
  open,
  onOpenChange,
}: LeaveDetailModalProps) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const isAdminOrHr =
    user?.role.mainRole === "admin" || user?.role.mainRole === "hr";
  const isLineManager = !!(
    user?.isLineManager || user?.role.subRole === "line_manager"
  );
  const canApprove = isAdminOrHr || isLineManager;
  const isOwn = !!request && String(request.employeeId) === String(user?.employeeId);

  const showTeamOverlap = canApprove && !isOwn && request?.status === "Pending";

  const { data: teamOverlap, isLoading: loadingOverlap } = useQuery<TeamLeaveOverlap>({
    queryKey: ["team-leave-overlap", request?.employeeId, request?.startDate, request?.endDate],
    queryFn: () =>
      apiGetTeamLeaveOverlap(
        request!.employeeId,
        request!.startDate,
        request!.endDate,
      ),
    enabled: open && showTeamOverlap && !!request,
    staleTime: 60_000,
  });

  const cancelMutation = useMutation({
    mutationFn: () => apiCancelLeaveRequest(request!.id),
    onSuccess: () => {
      toast.success(t("leave.requestCancelled"));
      queryClient.invalidateQueries({ queryKey: ["myLeaveRequests"] });
      queryClient.invalidateQueries({ queryKey: ["leaveRequests"] });
      queryClient.invalidateQueries({ queryKey: ["myLeaveBalance"] });
      onOpenChange(false);
    },
    onError: (err: Error) => toast.error(getLeaveApiErrorMessage(t, err)),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append("document", file);
      return apiUploadLeaveDocument(request!.id, formData);
    },
    onSuccess: (updated) => {
      toast.success(t("leave.documentUploadedForReview"));
      queryClient.invalidateQueries({ queryKey: ["myLeaveRequests"] });
      queryClient.invalidateQueries({ queryKey: ["leaveRequests"] });
      queryClient.invalidateQueries({ queryKey: ["leaveRequest", String(updated.id)] });
      onOpenChange(false);
    },
    onError: (err: Error) => toast.error(getLeaveApiErrorMessage(t, err)),
  });

  const reactivateMutation = useMutation({
    mutationFn: () => apiReactivateEmployee(request!.id),
    onSuccess: () => {
      toast.success(t("leave.extended.reactivationSuccess", "Employee reactivated successfully"));
      queryClient.invalidateQueries({ queryKey: ["leaveRequests"] });
      queryClient.invalidateQueries({ queryKey: ["myLeaveRequests"] });
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      onOpenChange(false);
    },
    onError: (err: Error) => toast.error(getLeaveApiErrorMessage(t, err)),
  });

  if (!request) return null;

  const isExtendedLeave = Boolean((request as any).isExtendedLeave);
  const employeeOnLeave = (request as any).employeeStatus === "On Leave";
  const canReactivate = isAdminOrHr && isExtendedLeave && request.status === "Approved" && employeeOnLeave;

  const canCancel = isOwn && request.status === "Pending";
  const needsEmployeeDocument = isOwn && request.status === "Pending Employee Document";
  const showActions =
    canCancel ||
    needsEmployeeDocument ||
    canReactivate ||
    (canApprove && !isOwn && ["Pending", "HR Review Required", "Document Uploaded"].includes(request.status));

  const docUrl = request.documentUrl
    ? `${API_BASE}${request.documentUrl}`
    : null;

  const leaveLabel = t(`leave.types.${request.leaveType}`, {
    defaultValue: request.leaveTypeLabel ?? request.leaveType,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden">
        <div className="px-6 pt-6 pb-5 bg-muted/30 border-b">
          <DialogHeader className="mb-4">
            <div className="flex items-center justify-between gap-3 pr-8">
              <DialogTitle className="text-base font-semibold leading-tight">
                {t("leave.requestTitle")}{" "}
                <span className="text-muted-foreground font-normal">
                  #{request.id}
                </span>
              </DialogTitle>
              <StatusPill status={request.status} label={getLeaveStatusLabel(t, request.status)} />
            </div>
          </DialogHeader>

          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full gradient-brand flex items-center justify-center text-primary-foreground text-sm font-bold shrink-0 select-none">
              {initials(request.employeeName)}
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground leading-tight">
                {request.employeeName}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <p className="text-xs text-muted-foreground">
                  {t("leave.employee")}
                </p>
                {employeeOnLeave && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-pink-500/15 text-pink-500 border border-pink-500/30">
                    <ShieldAlertIcon className="w-2.5 h-2.5" />
                    {t("leave.extended.limitedAccess", "Limited Access")}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4 text-sm">
          <div className="rounded-lg border bg-card p-3.5 space-y-3">
            <div className="flex items-center gap-2">
              {request.leaveTypeColor ? (
                <span
                  className="inline-block h-3 w-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: request.leaveTypeColor }}
                />
              ) : (
                <span className="inline-block h-3 w-3 rounded-full flex-shrink-0 bg-primary" />
              )}
              <span className="font-medium text-foreground">{leaveLabel}</span>
            </div>

            <Separator />

            <div className="flex items-start gap-2.5">
              <CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <p className="font-medium text-foreground">
                  {fmt(request.startDate, dateLocale)}
                  {request.startDate !== request.endDate && (
                    <> - {fmt(request.endDate, dateLocale)}</>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {request.workingDays} {t("leave.workingDays").toLowerCase()}
                </p>
              </div>
            </div>

            {request.replacementEmployeeName && (
              <>
                <Separator />
                <div className="flex items-center gap-2.5">
                  <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">
                      {t("leave.coverage")}
                    </p>
                    <p className="font-medium text-foreground">
                      {request.replacementEmployeeName}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>

          {request.notes && (
            <div className="flex items-start gap-2.5 rounded-lg bg-muted/40 border p-3">
              <MessageSquare className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              <p className="text-muted-foreground leading-relaxed">
                {request.notes}
              </p>
            </div>
          )}

          {request.createdOnBehalf && (
            <div className="rounded-lg border bg-muted/40 p-3 text-sm">
              <p className="font-medium text-foreground">{t("leave.createdByHr")}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("leave.createdByOn", {
                  date: fmt(request.createdAt, dateLocale),
                  name: request.createdByName ?? t("leave.hrAdmin"),
                })}
              </p>
            </div>
          )}

          {request.status === "Pending Employee Document" && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3.5 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
              <p className="font-semibold">{t("leave.pendingEmployeeDocumentTitle")}</p>
              <p className="mt-1 text-xs">
                {request.requiredDocumentLabel ?? t("leave.supportingDocumentRequired")}
                {request.requiredDocumentDescription ? ` - ${request.requiredDocumentDescription}` : ""}
              </p>
            </div>
          )}

          {request.rejectionReason && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3.5">
              <p className="text-xs font-semibold text-destructive uppercase tracking-wide mb-1.5">
                {t("leave.rejectedReason")}
              </p>
              <p className="text-foreground leading-relaxed">
                {request.rejectionReason}
              </p>
            </div>
          )}

          {docUrl && (
            <a
              href={docUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <div className="group flex items-center gap-3 rounded-lg border bg-card p-3 hover:bg-muted/50 transition-colors cursor-pointer">
                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="h-4.5 w-4.5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {request.documentOriginalName ?? t("leave.document")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t("leave.viewDocument")}
                  </p>
                </div>
                <Download className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
              </div>
            </a>
          )}

          {showTeamOverlap && (
            <div className="rounded-lg border bg-muted/30 p-3.5 space-y-2.5">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm font-semibold text-foreground">{t("leave.teamAvailability")}</span>
              </div>
              {loadingOverlap ? (
                <div className="space-y-2 animate-pulse">
                  <div className="h-2.5 w-52 bg-muted rounded" />
                  <div className="h-2.5 w-40 bg-muted rounded" />
                </div>
              ) : teamOverlap ? (
                <>
                  <p className="text-xs text-muted-foreground">
                    {teamOverlap.teamSize === 0 ? t("leave.noTeamColleagues") : teamOverlap.onLeaveCount === 0
                      ? t("leave.teamAvailabilityAllWorking", { count: teamOverlap.teamSize })
                      : t("leave.teamAvailabilityOverlap", {
                          onLeave: teamOverlap.onLeaveCount,
                          total: teamOverlap.teamSize,
                          percent: Math.round((teamOverlap.onLeaveCount / teamOverlap.teamSize) * 100),
                          working: teamOverlap.workingCount,
                        })}
                  </p>
                  {teamOverlap.onLeave.length > 0 && (
                    <div className="space-y-1.5">
                      {teamOverlap.onLeave.map((entry) => (
                        <div key={entry.employeeId} className="flex items-center justify-between gap-2 text-xs">
                          <span className="font-medium text-foreground/80 truncate">{entry.employeeName}</span>
                          <div className="flex items-center gap-1.5 shrink-0 text-muted-foreground">
                            <span>{t(`leave.types.${entry.leaveType}`, { defaultValue: entry.leaveType })}</span>
                            <span>-</span>
                            <span>
                              {fmt(entry.startDate, dateLocale)}
                              {entry.startDate !== entry.endDate && <> - {fmt(entry.endDate, dateLocale)}</>}
                            </span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              entry.status === "Approved"
                                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                            }`}>
                              {getLeaveStatusLabel(t, entry.status)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          )}

          <div className="space-y-1.5 pt-1">
            {(request.reviewedByName || request.reviewedAt) && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                <span>
                  {t("leave.reviewedBy")} {request.reviewedByName}
                  {request.reviewedAt && ` - ${fmt(request.reviewedAt, dateLocale)}`}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CalendarDays className="h-3.5 w-3.5 flex-shrink-0" />
              <span>
                {t("leave.submitted")} {fmt(request.createdAt, dateLocale)}
              </span>
            </div>
          </div>
        </div>

        {showActions && (
          <div className="px-6 pb-6 pt-3 border-t bg-muted/20">
            {canCancel && !canApprove && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => cancelMutation.mutate()}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending
                  ? t("leave.cancelling")
                  : t("leave.cancelRequest")}
              </Button>
            )}

            {canApprove && !isOwn && request.status === "Pending" && (
              <div className="space-y-2">
                <LeaveApprovalButtons
                  requestId={request.id}
                  employeeId={request.employeeId}
                  onSuccess={() => onOpenChange(false)}
                  fullWidth
                />
                {canCancel && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-muted-foreground"
                    onClick={() => cancelMutation.mutate()}
                    disabled={cancelMutation.isPending}
                  >
                    {cancelMutation.isPending
                      ? t("leave.cancelling")
                      : t("leave.cancelRequest")}
                  </Button>
                )}
              </div>
            )}

            {canApprove && !isOwn && ["HR Review Required", "Document Uploaded"].includes(request.status) && (
              <LeaveApprovalButtons
                requestId={request.id}
                employeeId={request.employeeId}
                onSuccess={() => onOpenChange(false)}
                fullWidth
              />
            )}

            {needsEmployeeDocument && (
              <div>
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) uploadMutation.mutate(file);
                  }}
                />
                <Button
                  className="w-full gap-2"
                  onClick={() => uploadInputRef.current?.click()}
                  disabled={uploadMutation.isPending}
                >
                  <Upload className="h-4 w-4" />
                  {uploadMutation.isPending
                    ? t("leave.uploadingDocument")
                    : t("leave.uploadRequiredDocument")}
                </Button>
              </div>
            )}

            {canReactivate && (
              <div className="space-y-2">
                <div className="rounded-lg border border-pink-500/30 bg-pink-500/5 p-3 text-sm">
                  <p className="font-medium text-foreground">{
                    t("leave.extended.reactivateTitle", "Employee is currently on leave")
                  }</p>
                  <p className="text-xs text-muted-foreground mt-1">{
                    t("leave.extended.reactivateDesc", "Reactivating will restore the employee to Active status with full system access.")
                  }</p>
                </div>
                <Button
                  id={`reactivate-employee-btn-${request.id}`}
                  className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => reactivateMutation.mutate()}
                  disabled={reactivateMutation.isPending}
                >
                  <RotateCcw className="h-4 w-4" />
                  {reactivateMutation.isPending
                    ? t("leave.extended.reactivating", "Reactivating...")
                    : t("leave.extended.reactivateEmployee", "Reactivate Employee")}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
